import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { StandardURL } from '../services/constants';
import { FaWhatsapp, FaInstagram, FaFacebookF, FaMusic, FaTwitter, FaYoutube, FaSnapchatGhost } from 'react-icons/fa';

// ─── Inline Styles ────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg: #0a0a0a;
    --surface: #111111;
    --surface2: #1a1a1a;
    --border: #2a2a2a;
    --accent: #e8ff3c;
    --accent-dim: rgba(232,255,60,0.12);
    --text: #f0f0f0;
    --muted: #666666;
    --red: #ff4545;
    --green: #3cffa0;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
  }

  /* NAV */
  .nav {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(10,10,10,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2rem;
    height: 64px;
  }
  .nav-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.8rem;
    letter-spacing: 0.08em;
    color: var(--accent);
    text-decoration: none;
    cursor: pointer;
  }
  .nav-links {
    display: flex;
    gap: 2rem;
    list-style: none;
  }
  .nav-links a {
    color: var(--muted);
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    transition: color 0.2s;
    cursor: pointer;
  }
  .nav-links a:hover { color: var(--text); }
  .nav-right {
    display: flex;
    align-items: center;
    gap: 1.2rem;
  }
  .nav-cart {
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 4px;
    padding: 0.45rem 1rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    letter-spacing: 0.04em;
    transition: opacity 0.2s;
  }
  .nav-cart:hover { opacity: 0.85; }
  .nav-account {
    color: var(--muted);
    font-size: 0.8rem;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s;
  }
  .nav-account:hover { color: var(--text); }

  /* PROMO BANNER */
  .promo-banner {
    background: var(--accent);
    color: #000;
    text-align: center;
    padding: 0.55rem 1rem;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.05rem;
    letter-spacing: 0.12em;
    overflow: hidden;
    white-space: nowrap;
  }
  .promo-inner {
    display: inline-block;
    animation: marquee 18s linear infinite;
  }
  @keyframes marquee {
    0%   { transform: translateX(100vw); }
    100% { transform: translateX(-100%); }
  }

  /* HERO */
  .hero {
    padding: 5rem 2rem 3rem;
    max-width: 900px;
    margin: 0 auto;
    text-align: center;
  }
  .hero h1 {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(3.5rem, 10vw, 7rem);
    line-height: 1;
    letter-spacing: 0.04em;
    margin-bottom: 1rem;
  }
  .hero h1 span { color: var(--accent); }
  .hero p {
    color: var(--muted);
    font-size: 1rem;
    max-width: 480px;
    margin: 0 auto 2rem;
    line-height: 1.6;
  }
  .hero-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  .btn-primary {
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 4px;
    padding: 0.75rem 2rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.9rem;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: opacity 0.2s;
  }
  .btn-primary:hover { opacity: 0.85; }
  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* LICENSING MODAL */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.85);
    z-index: 300;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }
  .modal-box {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 2.5rem;
    max-width: 820px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
  }
  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: var(--surface2);
    border: 1px solid var(--border);
    color: var(--muted);
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.8rem;
    transition: color 0.2s;
  }
  .modal-close:hover { color: var(--text); }
  .modal-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2rem;
    letter-spacing: 0.06em;
    margin-bottom: 0.3rem;
  }
  .modal-title span { color: var(--accent); }
  .modal-subtitle {
    color: var(--muted);
    font-size: 0.85rem;
    margin-bottom: 2rem;
  }
  .license-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
  @media (max-width: 600px) { .license-grid { grid-template-columns: 1fr; } }
  .license-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 1.8rem;
    position: relative;
    overflow: hidden;
  }
  .license-card.premium { border-color: var(--accent); }
  .license-card.premium::before {
    content: 'PREMIUM';
    position: absolute;
    top: 14px;
    right: -22px;
    background: var(--accent);
    color: #000;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 0.75rem;
    letter-spacing: 0.1em;
    padding: 3px 32px;
    transform: rotate(45deg);
  }
  .license-type {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.5rem;
    letter-spacing: 0.06em;
    margin-bottom: 0.2rem;
  }
  .license-card.premium .license-type { color: var(--accent); }
  .license-desc {
    font-size: 0.78rem;
    color: var(--muted);
    margin-bottom: 1.2rem;
  }
  .license-perks {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }
  .license-perks li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.82rem;
    color: var(--text);
    line-height: 1.4;
  }
  .license-perks li .check { color: var(--green); flex-shrink: 0; margin-top: 1px; }
  .license-perks li.exclusive .check { color: var(--accent); }
  .license-perks li.exclusive { font-weight: 600; }

  /* PLAYER BAR */
  .player-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    background: var(--surface);
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 0.75rem 2rem;
    flex-wrap: wrap;
  }
  .player-art {
    width: 44px;
    height: 44px;
    background: var(--surface2);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    flex-shrink: 0;
  }
  .player-info { min-width: 140px; flex-shrink: 0; }
  .player-title { font-size: 0.85rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
  .player-artist { font-size: 0.75rem; color: var(--muted); }
  .player-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    justify-content: center;
  }
  .ctrl-btn {
    background: none;
    border: none;
    color: var(--muted);
    font-size: 1.1rem;
    cursor: pointer;
    padding: 4px;
    transition: color 0.2s;
  }
  .ctrl-btn:hover { color: var(--text); }
  .ctrl-btn.play {
    background: var(--accent);
    color: #000;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
  }
  .ctrl-btn.play:hover { opacity: 0.85; }
  .progress-wrap {
    flex: 1;
    max-width: 360px;
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }
  .progress-time { font-size: 0.72rem; color: var(--muted); width: 32px; text-align: center; }
  .progress-bar {
    flex: 1;
    height: 3px;
    background: var(--border);
    border-radius: 2px;
    cursor: pointer;
    position: relative;
  }
  .progress-fill {
    height: 100%;
    background: var(--accent);
    border-radius: 2px;
    transition: width 0.1s linear;
  }
  .player-speed {
    background: var(--surface2);
    color: var(--muted);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 2px 7px;
    font-size: 0.72rem;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
  }

  /* FILTER BAR */
  .filter-bar {
    max-width: 960px;
    margin: 2rem auto 1.5rem;
    padding: 0 2rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .filter-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--muted);
    font-weight: 600;
  }
  .filter-chip {
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--muted);
    border-radius: 100px;
    padding: 0.35rem 1rem;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.15s;
    font-family: 'DM Sans', sans-serif;
  }
  .filter-chip:hover, .filter-chip.active {
    background: var(--accent-dim);
    border-color: var(--accent);
    color: var(--accent);
  }
  .search-input {
    margin-left: auto;
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--text);
    border-radius: 4px;
    padding: 0.45rem 1rem;
    font-size: 0.85rem;
    font-family: 'DM Sans', sans-serif;
    width: 200px;
    outline: none;
    transition: border-color 0.2s;
  }
  .search-input:focus { border-color: var(--accent); }
  .search-input::placeholder { color: var(--muted); }

  /* BEAT LIST */
  .beat-list {
    max-width: 960px;
    margin: 0 auto;
    padding: 0 2rem 140px;
  }

  .beat-row {
    display: grid;
    grid-template-columns: 50px 1fr auto auto;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    border-bottom: 1px solid var(--border);
    cursor: pointer;
    transition: background 0.15s;
  }

  .beat-row:hover { background: var(--surface); }
  .beat-row.playing { background: var(--accent-dim); }

  .beat-index {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: var(--accent);
  }

  .beat-main { display: flex; align-items: center; gap: 1rem; min-width: 0; overflow: hidden; }
  .beat-thumb {
    width: 42px;
    height: 42px;
    background: var(--surface2);
    border-radius: 4px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .beat-name { font-weight: 500; font-size: 0.9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .beat-meta { font-size: 0.75rem; color: var(--muted); margin-top: 2px; }

  .yt-icon-link {
    display: flex;
    align-items: center;
    color: #ff0000;
    transition: transform 0.2s, opacity 0.2s;
  }
  .yt-icon-link:hover {
    transform: scale(1.1);
    opacity: 0.8;
  }

  .beat-prices {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    align-items: flex-end;
  }

  .price-btn {
    border: none;
    border-radius: 4px;
    padding: 0.3rem 0.85rem;
    font-weight: 600;
    font-size: 0.75rem;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    white-space: nowrap;
    transition: opacity 0.15s, filter 0.2s;
    position: relative;
  }
  .price-btn:hover:not(:disabled):not(.blurred) { opacity: 0.85; }
  .price-btn.lease {
    background: var(--surface2);
    color: var(--text);
    border: 1px solid var(--border);
  }
  .price-btn.lease:hover:not(:disabled):not(.blurred) { border-color: var(--text); }
  .price-btn.premium-btn {
    background: var(--accent);
    color: #000;
  }
  .price-btn.added {
    opacity: 0.5;
    cursor: default;
  }
  .price-btn.blurred {
    filter: blur(3px);
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* --- MOBILE RESPONSIVE UPGRADE --- */
  @media (max-width: 768px) {
    .beat-row {
      grid-template-columns: 40px 1fr auto; 
      gap: 0.5rem;
    }
    
    .yt-icon-link { display: none; }

    .beat-prices {
      grid-column: 1 / -1; 
      flex-direction: row; 
      width: 100%;
      margin-top: 0.5rem;
      gap: 0.5rem;
    }

    .price-btn {
      flex: 1;
      padding: 0.8rem 0;
    }
  }

  /* SECTION TITLE */
  .section-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.6rem;
    letter-spacing: 0.06em;
    color: var(--text);
    max-width: 960px;
    margin: 0 auto 1rem;
    padding: 0 2rem;
  }
  .section-title span { color: var(--accent); }

  /* STATE MSG */
  .state-msg {
    color: var(--muted);
    padding: 3rem 0;
    text-align: center;
    font-size: 0.9rem;
  }

  /* FOOTER */
  .footer {
    background: var(--surface);
    border-top: 1px solid var(--border);
    text-align: center;
    padding: 2rem;
    font-size: 0.8rem;
    color: var(--muted);
    margin-bottom: 80px;
  }
  .footer a { color: var(--muted); text-decoration: none; }
  .footer a:hover { color: var(--text); }
  .footer-brand { margin-top: 0.8rem; font-size: 0.72rem; color: #444; }
  .footer-brand a { color: var(--accent); font-weight: 600; text-decoration: none; }
  .footer-brand a:hover { text-decoration: underline; }

  /* CART DRAWER */
  .cart-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    z-index: 250;
  }
  .cart-drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 360px;
    background: var(--surface);
    border-left: 1px solid var(--border);
    z-index: 260;
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
  }
  .cart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }
  .cart-header h2 {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.06em;
  }
  .cart-close {
    background: none;
    border: 1px solid var(--border);
    color: var(--muted);
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: color 0.2s;
  }
  .cart-close:hover { color: var(--text); }
  .cart-items { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 0.75rem; }
  .cart-item {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 0.85rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }
  .cart-item-info { flex: 1; min-width: 0; }
  .cart-item-name { font-size: 0.85rem; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cart-item-type { font-size: 0.72rem; color: var(--muted); margin-top: 2px; }
  .cart-item-price { font-size: 0.85rem; font-weight: 600; color: var(--accent); white-space: nowrap; }
  .cart-remove {
    background: none;
    border: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 1rem;
    transition: color 0.15s;
  }
  .cart-remove:hover { color: var(--red); }
  .cart-footer { border-top: 1px solid var(--border); padding-top: 1rem; margin-top: 1rem; }
  .cart-total {
    display: flex;
    justify-content: space-between;
    font-weight: 600;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }
  .cart-empty { color: var(--muted); text-align: center; padding: 2rem 0; font-size: 0.85rem; }
  .cart-limit-note {
    font-size: 0.72rem;
    color: var(--muted);
    text-align: center;
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    background: var(--surface2);
    border-radius: 4px;
    border: 1px solid var(--border);
  }

  /* NAV ORDERS BUTTON */
  .nav-orders-btn {
    background: var(--surface2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0.45rem 1rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    letter-spacing: 0.04em;
    transition: border-color 0.2s, color 0.2s;
  }
  .nav-orders-btn:hover { border-color: var(--accent); color: var(--accent); }

  /* ORDERS DRAWER */
  .orders-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    z-index: 250;
  }
  .orders-drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 460px;
    background: var(--surface);
    border-left: 1px solid var(--border);
    z-index: 260;
    display: flex;
    flex-direction: column;
  }
  .orders-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .orders-drawer-header h2 {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.06em;
  }
  .orders-tabs {
    display: flex;
    gap: 0;
    padding: 1rem 1.5rem 0;
    flex-shrink: 0;
    border-bottom: 1px solid var(--border);
  }
  .orders-tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--muted);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.6rem 1.2rem;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;
    margin-bottom: -1px;
  }
  .orders-tab:hover { color: var(--text); }
  .orders-tab.active {
    color: var(--accent);
    border-bottom-color: var(--accent);
  }
  .orders-tab .tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--accent-dim);
    color: var(--accent);
    border-radius: 100px;
    font-size: 0.65rem;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    margin-left: 6px;
  }
  .orders-list {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .orders-empty {
    color: var(--muted);
    text-align: center;
    padding: 3rem 0;
    font-size: 0.85rem;
  }
  .orders-empty-icon { font-size: 2.5rem; margin-bottom: 0.75rem; }
  .order-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 1rem 1.1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: border-color 0.15s;
  }
  .order-card:hover { border-color: #3a3a3a; }
  .order-card-icon {
    width: 42px;
    height: 42px;
    background: var(--surface);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    flex-shrink: 0;
  }
  .order-card-body { flex: 1; min-width: 0; }
  .order-card-name {
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 3px;
  }
  .order-card-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .order-card-amount {
    font-size: 0.78rem;
    color: var(--accent);
    font-weight: 700;
  }
  .order-card-version {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 100px;
    padding: 1px 8px;
  }
  .order-card-date {
    font-size: 0.7rem;
    color: var(--muted);
  }
  .order-card-status {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 100px;
  }
  .order-card-status.pending {
    background: rgba(255,165,0,0.12);
    color: #ffaa00;
    border: 1px solid rgba(255,165,0,0.3);
  }
  .order-card-status.complete {
    background: rgba(60,255,160,0.1);
    color: var(--green);
    border: 1px solid rgba(60,255,160,0.25);
  }
  .order-card-actions { display: flex; flex-direction: column; gap: 0.4rem; flex-shrink: 0; }
  .btn-pay-pending {
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 5px;
    padding: 0.38rem 0.9rem;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s;
    letter-spacing: 0.03em;
  }
  .btn-pay-pending:hover { opacity: 0.85; }
  
  .orders-refresh {
    background: none;
    border: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 0.85rem;
    padding: 4px 6px;
    border-radius: 4px;
    transition: color 0.2s;
    margin-right: 0.5rem;
  }
  .orders-refresh:hover { color: var(--accent); }
  .orders-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 0;
    gap: 1rem;
    color: var(--muted);
    font-size: 0.85rem;
  }
  .orders-spinner {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @media (max-width: 768px) {
    .orders-drawer { width: 100%; }
  }

  /* CHECKOUT MODAL */
  .checkout-modal-box {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 2.5rem;
    max-width: 480px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
  }
  .checkout-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.8rem;
    letter-spacing: 0.06em;
    margin-bottom: 0.25rem;
  }
  .checkout-title span { color: var(--accent); }
  .checkout-subtitle {
    color: var(--muted);
    font-size: 0.82rem;
    margin-bottom: 1.75rem;
  }
  .checkout-summary {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 1rem 1.2rem;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .checkout-summary-name {
    font-weight: 600;
    font-size: 0.9rem;
  }
  .checkout-summary-meta {
    font-size: 0.75rem;
    color: var(--muted);
    margin-top: 2px;
  }
  .checkout-summary-price {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.5rem;
    color: var(--accent);
    white-space: nowrap;
  }
  .checkout-fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  .checkout-field label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
    font-weight: 600;
    margin-bottom: 0.4rem;
  }
  .checkout-field input {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--border);
    color: var(--text);
    border-radius: 6px;
    padding: 0.65rem 1rem;
    font-size: 0.88rem;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.2s;
  }
  .checkout-field input:focus { border-color: var(--accent); }
  .checkout-field input::placeholder { color: var(--muted); }
  .checkout-field input:read-only {
    opacity: 0.6;
    cursor: default;
  }
  .checkout-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  .checkout-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .btn-delete-order {
    background: transparent;
    color: var(--red);
    border: 1px solid rgba(255, 69, 69, 0.3);
    border-radius: 5px;
    padding: 0.38rem 0.9rem;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, border-color 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }
  .btn-delete-order:hover:not(:disabled) {
    background: rgba(255, 69, 69, 0.1);
    border-color: var(--red);
  }
  .btn-delete-order:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn-ghost {
    background: none;
    border: 1px solid var(--border);
    color: var(--muted);
    border-radius: 4px;
    padding: 0.75rem 1.5rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;
    flex: 1;
  }
  .btn-ghost:hover { color: var(--text); border-color: var(--text); }
  .btn-checkout-submit {
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 4px;
    padding: 0.75rem 1.5rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: opacity 0.2s;
    flex: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  .btn-checkout-submit:hover:not(:disabled) { opacity: 0.85; }
  .btn-checkout-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .checkout-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(0,0,0,0.3);
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 768px) {
    .nav { padding: 0 0.75rem; height: 56px; }
    .nav-links { display: none; }
    .nav-logo { font-size: 1.4rem; }
    .nav-cart { padding: 0.4rem 0.65rem; font-size: 0.72rem; }
    .nav-orders-btn { padding: 0.4rem 0.65rem; font-size: 0.72rem; }

    .hero { padding: 3rem 1rem 2rem; }

    .filter-bar { padding: 0 1rem; gap: 0.5rem; }
    .search-input { width: 100%; margin-left: 0; }

    .beat-list { padding: 0 1rem 160px; }

    .player-bar { gap: 0.75rem; padding: 0.6rem 1rem; }
    .player-info { display: none; }
    .progress-wrap { max-width: 100%; }

    .cart-drawer { width: 100%; }

    .modal-box { padding: 1.5rem; }
    .checkout-modal-box { padding: 1.5rem; }
    .checkout-row { grid-template-columns: 1fr; }
    .checkout-actions { flex-direction: column; }
    .btn-ghost, .btn-checkout-submit { flex: none; width: 100%; }
  }

  @media (max-width: 480px) {
    .nav-right { gap: 0.4rem; }
    .nav-logo { font-size: 1.2rem; }
    .nav-orders-btn { padding: 0.35rem 0.5rem; font-size: 0.68rem; letter-spacing: 0; }
    .nav-cart { padding: 0.35rem 0.5rem; font-size: 0.68rem; letter-spacing: 0; }
    .nav-account { display: none; }
    .order-card { flex-wrap: wrap; }
    .order-card-actions { width: 100%; flex-direction: row; }
    .btn-pay-pending { flex: 1; justify-content: center; }
  }

  /* PESAPAL IFRAME MODAL */
  .pesapal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.92);
    z-index: 400;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }
  .pesapal-modal {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    width: 100%;
    max-width: 720px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  }
  .pesapal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .pesapal-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .pesapal-logo-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--green);
    animation: pulse-dot 1.8s ease-in-out infinite;
  }
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
  }
  .pesapal-header-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.1rem;
    letter-spacing: 0.08em;
  }
  .pesapal-header-title span { color: var(--accent); }
  .pesapal-header-secure {
    font-size: 0.7rem;
    color: var(--muted);
    margin-top: 1px;
  }
  .pesapal-close-btn {
    background: var(--surface2);
    border: 1px solid var(--border);
    color: var(--muted);
    border-radius: 4px;
    padding: 5px 12px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.78rem;
    transition: color 0.2s;
  }
  .pesapal-close-btn:hover { color: var(--red); border-color: var(--red); }
  .pesapal-iframe-wrap {
    position: relative;
    width: 100%;
    height: 560px;
    background: #fff;
  }
  .pesapal-iframe-wrap iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
  .pesapal-loading-cover {
    position: absolute;
    inset: 0;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    z-index: 1;
    transition: opacity 0.3s;
  }
  .pesapal-loading-cover.hidden { opacity: 0; pointer-events: none; }
  .pesapal-iframe-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #e0e0e0;
    border-top-color: #333;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  .pesapal-loading-text {
    color: #555;
    font-size: 0.85rem;
    font-family: 'DM Sans', sans-serif;
  }
    /* PHOTO ZOOM MODAL */
  .photo-zoom-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.92);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    cursor: zoom-out;
  }
  .photo-zoom-overlay img {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    object-fit: contain;
    cursor: default;
  }
  .photo-zoom-close {
    position: absolute;
    top: 1.5rem;
    right: 2rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    border-radius: 4px;
    padding: 8px 16px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.2s;
  }
  .photo-zoom-close:hover {
    background: rgba(255, 69, 69, 0.2);
    border-color: #ff4545;
    color: #ff4545;
  }
  .pesapal-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-top: 1px solid var(--border);
    font-size: 0.72rem;
    color: var(--muted);
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .pesapal-modal { max-width: 100%; border-radius: 8px; }
    .pesapal-iframe-wrap { height: 480px; }
  }
  @media (max-width: 480px) {
    .pesapal-iframe-wrap { height: 420px; }
    .pesapal-header { padding: 0.75rem 1rem; }
  }
`;

// ─── License Perks ────────────────────────────────────────────────────────────
const LEASE_PERKS = [
  "MP3, Wave + Stems",
  "Unlimited copies to DSPs (Spotify, Apple Music etc.)",
  "Unlimited audio streams",
  "Unlimited music videos",
  "Unlimited radio broadcast",
  "Unlimited free download",
  "Unlimited video streams",
  "For profit performance",
  "Allows rearrangement",
  "50/50 split on PRO",
  "Must credit producer: Starr Kun",
];
const PREMIUM_PERKS = [...LEASE_PERKS, "Exclusive Ownership"];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(secs) {
  const m = Math.floor(secs / 60);
  const s = String(Math.floor(secs % 60)).padStart(2, "0");
  return `${m}:${s}`;
}

const getResourceSrc = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${StandardURL}${url}`;
};

// ─── Component ────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();

  // Auth
  const token          = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData       = userDataString ? JSON.parse(userDataString) : {};
  const userId         = userData.unique || "";
  const isLoggedIn     = !!token;

  // Data
  const [beats, setBeats]                   = useState([]);
  const [categories, setCategories]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch]                 = useState("");

  // Promo code states
  const [promoCodeInput, setPromoCodeInput]         = useState("");
  const [discountAmount, setDiscountAmount]         = useState(0);
  const [promoValidating, setPromoValidating]       = useState(false);
  const [promoAppliedMessage, setPromoAppliedMessage] = useState(null);

  // Photo Zoom State
  const [zoomedPhoto, setZoomedPhoto] = useState(null);

  // Player
  const [playing, setPlaying]     = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [elapsed, setElapsed]     = useState(0);
  const [duration, setDuration]   = useState(0);
  const audioRef                  = useRef(null);

  // UI
  const [cart, setCart]               = useState([]);
  const [cartOpen, setCartOpen]       = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);

  // Checkout
  const [checkoutOpen, setCheckoutOpen]       = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [phone, setPhone]                     = useState(userData.phone || "0113060715");
  const [pesapalUrl, setPesapalUrl]           = useState(null);
  const [iframeLoaded, setIframeLoaded]       = useState(false);

  // Orders drawer
  const [ordersOpen, setOrdersOpen]           = useState(false);
  const [ordersTab, setOrdersTab]             = useState("pending");
  const [pendingOrders, setPendingOrders]     = useState([]);
  const [completeOrders, setCompleteOrders]   = useState([]);
  const [ordersLoading, setOrdersLoading]     = useState(false);
  const [deletingId, setDeletingId]           = useState(null);

  // Pending order checkout
  const [pendingCheckoutItem, setPendingCheckoutItem]   = useState(null);
  const [pendingCheckoutOpen, setPendingCheckoutOpen]   = useState(false);

  const [socialLinks, setSocialLinks] = useState(null);

  useEffect(() => {
    fetchSocialMedia();
  }, []);

  const fetchSocialMedia = async () => {
    try {
      const response = await fetch(`${StandardURL}/iMusic/user/socialmedia/listadmin`, {
        headers: {
          'Authorization': `Bearer ${token}` 
        }
      });
      const data = await response.json();
      if (data?.response && data.response.length > 0) {
        setSocialLinks(data.response[0]);
      }
    } catch (error) {
      console.error('Error fetching social media:', error);
    }
  };

  // ── Fetch on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [beatsRes, catsRes] = await Promise.all([
          axios.get(`${StandardURL}/iMusic/user/music/list`),
          axios.get(`${StandardURL}/iMusic/user/category/listadmin`),
        ]);
        setBeats(beatsRes.data.response || beatsRes.data || []);
        setCategories(catsRes.data.response || catsRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load beats.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── Audio event listeners ───────────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => {
      if (audio.duration) {
        setElapsed(audio.currentTime);
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnded  = () => { setIsPlaying(false); setProgress(0); setElapsed(0); };
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  // ── Category filter ─────────────────────────────────────────────────────────
  const handleCategoryFilter = async (cat) => {
    setActiveCategory(cat);
    setLoading(true);
    try {
      if (cat === "All") {
        const res = await axios.get(`${StandardURL}/iMusic/user/music/list`);
        setBeats(res.data.response || res.data || []);
      } else {
        const res = await axios.post(
          `${StandardURL}/iMusic/user/music/getbycategory`,
          { category: cat }
        );
        setBeats(res.data.response || res.data || []);
      }
    } catch (e) {
      toast.error("Failed to filter beats by that category.");
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePromoCode = async () => {
    if (!promoCodeInput.trim()) {
      toast.error("Please provide a promo code");
      return;
    }
    setPromoValidating(true);
    try {
      const res = await axios.post(`${StandardURL}/iMusic/promo-codes/validate`, {
        promocodeamount: promoCodeInput.trim()
      });
      const resData = res.data?.response || res.data;
      const amt = Number(resData?.amount || 0);
      const msg = resData?.message || "Promocode is valid";

      setDiscountAmount(amt);
      setPromoAppliedMessage(msg);
      toast.success(msg);
    } catch (error) {
      const errData = error?.response?.data?.response || error?.response?.data;
      const errMsg = errData?.message || "The promocode is expired";
      setDiscountAmount(0);
      setPromoAppliedMessage(errMsg);
      toast.error(errMsg);
    } finally {
      setPromoValidating(false);
    }
  };

  // ── Player ──────────────────────────────────────────────────────────────────
  const currentBeat = beats.find((b) => b.id === playing) || null;

  const handlePlay = (beat) => {
    if (playing === beat.id) {
      if (audioRef.current) {
        isPlaying ? audioRef.current.pause() : audioRef.current.play();
      }
      setIsPlaying((p) => !p);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    setPlaying(beat.id);
    setIsPlaying(true);
    setProgress(0);
    setElapsed(0);
    setDuration(0);
    setTimeout(() => {
      if (audioRef.current && beat.music) {
        const finalUrl = getResourceSrc(beat.music);
        audioRef.current.src = finalUrl;
        audioRef.current.load();
        audioRef.current.play().catch(() => {
          toast.error("Audio not found.");
        });
      }
    }, 50);
  };

  const handlePrev = () => {
    const idx = beats.findIndex((b) => b.id === playing);
    if (idx > 0) handlePlay(beats[idx - 1]);
  };
  const handleNext = () => {
    const idx = beats.findIndex((b) => b.id === playing);
    if (idx < beats.length - 1) handlePlay(beats[idx + 1]);
  };

  // ── Add to cart ────────────────────────────────--------
  const handleAddToCart = async (beat, type, e) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    if (cart.length > 0) {
      toast.info("You can only checkout one beat at a time. Remove the current item first.");
      return;
    }

    const amount  = type === "premium" ? beat.pricepremium : beat.price;
    const cartKey = `${beat.id}-${type}`;

    try {
      await axios.post(
        `${StandardURL}/iMusic/user/order/add/${userId}`,
        { amount, productid: beat.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart([{ ...beat, cartKey, licenseType: type, cartAmount: amount }]);
      toast.success(`${type === "premium" ? "Premium" : "Lease"} license added!`);
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart.");
    }
  };

  const removeFromCart = (cartKey) =>
    setCart((prev) => prev.filter((c) => c.cartKey !== cartKey));

  const totalCart = cart.reduce((s, c) => s + Number(c.cartAmount), 0);

  const filtered = beats.filter((b) => {
    const name = (b.title || b.name || b.music || "").toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const beatName = (beat) =>
    beat.title || beat.name || beat.music?.split("/").pop()?.split("?")[0] || "Untitled";

  // ── Checkout ────────────────────────────────────────────────────────────────
  const cartItem = cart[0] || null;
  const originalCartAmount = cartItem ? Number(cartItem.cartAmount) : 0;
  const finalCartAmount = Math.max(0, originalCartAmount - discountAmount);

  const handleCheckout = async () => {
    if (!cartItem) return;

    setCheckoutLoading(true);
    try {
      const payload = {
        phone:           userData.phone || phone || "0113060715",
        amount:          originalCartAmount,
        fname:           userData.fullName || "",
        lname:           userData.fullName || "",
        email:           userData.email    || "",
        version:         cartItem.licenseType === "premium" ? "premium" : "lease",
        productid:       cartItem.id,
        promocodeamount: discountAmount,
      };

      const res = await axios.post(
        `${StandardURL}/iMusic/user/payment/buybeats/${userId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payUrl = res.data?.response;

      if (res.data?.success && payUrl) {
        setCheckoutOpen(false);
        setPesapalUrl(payUrl);          
        setCart([]);
        setCartOpen(false);
        setPromoCodeInput("");
        setDiscountAmount(0);
        setPromoAppliedMessage(null);
      } else {
        toast.error("Could not retrieve payment link. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  // ── Fetch orders ─────────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    if (!isLoggedIn) return;
    
    setOrdersLoading(true);

    try {
      const pendRes = await axios.get(`${StandardURL}/iMusic/user/order/listuserpending/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const pendingArray = pendRes.data?.response || pendRes.data || [];
      setPendingOrders(pendingArray);
    } catch (err) {
      setPendingOrders([]); 
    }

    try {
      const compRes = await axios.get(`${StandardURL}/iMusic/user/order/listusercomplete/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const completeArray = compRes.data?.response || compRes.data || [];
      setCompleteOrders(completeArray);
    } catch (err) {
      setCompleteOrders([]); 
    }

    setOrdersLoading(false);
  };

  const handleOpenOrders = () => {
    setOrdersOpen(true);
    fetchOrders();
  };

  // ── Pay pending order ────────────────────────────────────────────────────────
  const handlePayPending = (order) => {
    setPendingCheckoutItem(order);
    setPendingCheckoutOpen(true);
    setOrdersOpen(false);
    setPromoCodeInput("");
    setDiscountAmount(0);
    setPromoAppliedMessage(null);
  };

  const originalPendingAmount = pendingCheckoutItem ? Number(pendingCheckoutItem.amount || 0) : 0;
  const finalPendingAmount = Math.max(0, originalPendingAmount - discountAmount);

  const handlePendingCheckout = async () => {
    if (!pendingCheckoutItem) return;
    setCheckoutLoading(true);
    try {
      const payload = {
        orderid:         pendingCheckoutItem.orderid,
        phone:           userData.phone || phone || "0113060715",
        amount:          originalPendingAmount,
        fname:           userData.fullName || "",
        lname:           userData.fullName || "",
        email:           userData.email    || "",
        version:         pendingCheckoutItem.version || pendingCheckoutItem.licenseType || "lease",
        productid:       pendingCheckoutItem.id,
        promocodeamount: discountAmount,
      };
      
      const res = await axios.post(
        `${StandardURL}/iMusic/user/payment/paypending/${userId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const payUrl = res.data?.response;
      
      if (res.data?.success && payUrl) {
        setPendingCheckoutOpen(false);
        setPendingCheckoutItem(null);
        setPesapalUrl(payUrl);
        setIframeLoaded(false);
        setPromoCodeInput("");
        setDiscountAmount(0);
        setPromoAppliedMessage(null);
      } else {
        toast.error("Could not retrieve payment link. Please try again.");
      }
    } catch (error) {
      console.error("Pending checkout error:", error);
      toast.error(error?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  // ── Delete pending order ──────────────────────────────────────────────────────
  const handleDeleteOrder = async (order) => {
    if (!window.confirm("Are you sure you want to delete this pending order?")) return;
    
    setDeletingId(order.id);
    try {
      await axios.delete(
        `${StandardURL}/iMusic/user/order/delete/${order.id}/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Order deleted successfully!");
      setPendingOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (error) {
      console.error("Delete order error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete order.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatExternalUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  };

  const cartedBeatId   = cartItem ? cartItem.id   : null;
  const isLeaseAdded   = (beat) => cartItem && cartItem.id === beat.id && cartItem.licenseType === "lease";
  const isPremAdded    = (beat) => cartItem && cartItem.id === beat.id && cartItem.licenseType === "premium";
  const isLeaseBlurred = (beat) => cartItem && cartItem.id === beat.id && cartItem.licenseType === "premium";
  const isPremBlurred  = (beat) => cartItem && cartItem.id === beat.id && cartItem.licenseType === "lease";
  const isBeatLocked   = (beat) => cartItem && cartItem.id !== beat.id;

  return (
    <>
      <style>{styles}</style>
      <audio ref={audioRef} style={{ display: "none" }} />

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav className="nav">
        <span className="nav-logo">MUSICA</span>
        <ul className="nav-links">
          <li><a href="#">Beats</a></li>
          <li><a onClick={() => setLicenseOpen(true)}>Licensing</a></li>
          {isLoggedIn && <li><a onClick={handleOpenOrders}>My Orders</a></li>}
        </ul>
        <div className="nav-right">
          <a className="nav-account" onClick={() => navigate("/registration")}>
            My Account
          </a>
          <a className="nav-account" onClick={() => navigate("/logout")}>
            Log Out
          </a>
          {isLoggedIn && (
            <button className="nav-orders-btn" onClick={handleOpenOrders}>
              📋 My Orders
            </button>
          )}
          {isLoggedIn && (
            <button className="nav-cart" onClick={() => setCartOpen(true)}>
              🛒 {cart.length > 0 ? `$${totalCart.toFixed(2)} (${cart.length})` : "Cart (0)"}
            </button>
          )}
        </div>
      </nav>

      {/* ── PROMO BANNER ────────────────────────────────────────────────────── */}
      <div className="promo-banner">
        <span className="promo-inner">
          🔥 25 BEATS FOR $2500 🔥 LIMITED TIME OFFER &nbsp;&nbsp;&nbsp;
          🔥 25 BEATS FOR $2500 🔥 LIMITED TIME OFFER &nbsp;&nbsp;&nbsp;
          🔥 25 BEATS FOR $2500 🔥 LIMITED TIME OFFER
        </span>
      </div>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="hero">
        <h1>AVAILABLE <span>BEATS</span></h1>
        <p>Premium instrumentals for artists ready to make their mark. License a beat and start creating today.</p>
        <div className="hero-actions">
          <button className="btn-primary" onClick={() => setLicenseOpen(true)}>
            View Licensing
          </button>
        </div>
      </section>

      {/* ── FILTER BAR ──────────────────────────────────────────────────────── */}
      <div className="filter-bar">
        <span className="filter-label">Filter by</span>
        <button
          className={`filter-chip${activeCategory === "All" ? " active" : ""}`}
          onClick={() => handleCategoryFilter("All")}
        >
          All
        </button>
        {categories.map((cat) => {
          const catName = cat.category || cat.name || cat;
          return (
            <button
              key={catName}
              className={`filter-chip${activeCategory === catName ? " active" : ""}`}
              onClick={() => handleCategoryFilter(catName)}
            >
              {catName}
            </button>
          );
        })}
        <input
          className="search-input"
          type="text"
          placeholder="Search beats..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ── BEAT LIST ───────────────────────────────────────────────────────── */}
      <div className="beat-list">
        <p className="section-title">ALL <span>BEATS</span></p>

        {loading && <p className="state-msg">Loading beats...</p>}
        {!loading && filtered.length === 0 && (
          <p className="state-msg">No beats found.</p>
        )}

        {!loading && filtered.map((beat) => {
          const isRowPlaying  = playing === beat.id;
          const leaseAdded    = isLeaseAdded(beat);
          const premAdded     = isPremAdded(beat);
          const leaseBlurred  = isLeaseBlurred(beat);
          const premBlurred   = isPremBlurred(beat);
          const beatLocked    = isBeatLocked(beat);

          return (
            <div
              key={beat.id}
              className={`beat-row ${isRowPlaying ? "playing" : ""}`}
              onClick={() => handlePlay(beat)}
            >
              <div className="beat-index">
                {isRowPlaying && isPlaying ? "⏸" : "▶"}
              </div>

              <div className="beat-main">
                <div className="beat-thumb">
                  {beat.photo ? (
                    <img 
                      src={getResourceSrc(beat.photo)} 
                      alt="cover" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} 
                      onClick={(e) => {
                        e.stopPropagation(); 
                        setZoomedPhoto(getResourceSrc(beat.photo));
                      }}
                    />
                  ) : "🎵"}
                </div>
                <div>
                  <div className="beat-name">{beatName(beat)}</div>
                  <div className="beat-meta">{beat.category || ""}</div>
                </div>
              </div>

              <div className="beat-yt">
                {beat.youtube && (
                  <a
                    href={formatExternalUrl(beat.youtube)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="yt-icon-link"
                    onClick={(e) => e.stopPropagation()} 
                    title="Watch on YouTube"
                  >
                    <FaYoutube size={24} />
                  </a>
                )}
              </div>

              <div className="beat-prices" onClick={(e) => e.stopPropagation()}>
                <button
                  className={`price-btn lease ${leaseAdded ? "added" : ""} ${leaseBlurred ? "blurred" : ""}`}
                  disabled={leaseAdded || leaseBlurred || beatLocked}
                  onClick={(e) => !leaseAdded && !leaseBlurred && !beatLocked && handleAddToCart(beat, "lease", e)}
                  title={leaseBlurred ? "Remove premium to select lease" : "Lease license"}
                >
                  {leaseAdded ? "✓ Leased" : `Lease · $${Number(beat.price).toFixed(2)}`}
                </button>

                <button
                  className={`price-btn premium-btn ${premAdded ? "added" : ""} ${premBlurred ? "blurred" : ""}`}
                  disabled={premAdded || premBlurred || beatLocked}
                  onClick={(e) => !premAdded && !premBlurred && !beatLocked && handleAddToCart(beat, "premium", e)}
                  title={premBlurred ? "Remove lease to select premium" : "Premium license"}
                >
                  {premAdded ? "✓ Premium" : `Premium · $${Number(beat.pricepremium).toFixed(2)}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="footer">
        <p>© {new Date().getFullYear()} Starrkun · Beat Leasing | Free Downloads</p>
        <p style={{ marginTop: "0.4rem" }}>
          <a href="#">About</a> · <a href="#">Contact</a> · <a href="#">FAQ</a>
        </p>

        {socialLinks && (
          <div className="footer-socials" style={{ margin: "1.5rem 0", display: "flex", justifyContent: "center", gap: "1.5rem" }}>
            {socialLinks.whatsapp && (
              <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaWhatsapp size={22} />
              </a>
            )}
            {socialLinks.instagram && (
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaInstagram size={22} />
              </a>
            )}
            {socialLinks.facebook && (
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaFacebookF size={20} />
              </a>
            )}
            {socialLinks.x && (
              <a href={socialLinks.x} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaTwitter size={22} />
              </a>
            )}
            {socialLinks.youtube && (
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaYoutube size={24} />
              </a>
            )}
            {socialLinks.snapchat && (
              <a href={socialLinks.snapchat} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaSnapchatGhost size={22} />
              </a>
            )}
            {socialLinks.beatstars && (
              <a href={socialLinks.beatstars} target="_blank" rel="noopener noreferrer" className="social-icon">
                <FaMusic size={22} />
              </a>
            )}
          </div>
        )}

        <p className="footer-brand">
          A product developed by{" "}
          <a href="https://reivaxsoftwares.co.ke/" target="_blank" rel="noopener noreferrer">
            Reivax Softwares Limited
          </a>
        </p>
      </footer>

      {/* ── PLAYER BAR ──────────────────────────────────────────────────────── */}
      {currentBeat && (
        <div className="player-bar">
          <div className="player-art" style={{ overflow: 'hidden' }}>
            {currentBeat.photo ? (
              <img 
                src={getResourceSrc(currentBeat.photo)} 
                alt="cover" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              />
            ) : (
              "🎵"
            )}
          </div>
          <div className="player-info">
            <div className="player-title">{beatName(currentBeat)}</div>
            <div className="player-artist">{currentBeat.category || "Musica"}</div>
          </div>

          <div className="player-controls">
            <button className="ctrl-btn" onClick={handlePrev}>⏮</button>
            <button
              className="ctrl-btn play"
              onClick={() => {
                if (audioRef.current) {
                  isPlaying ? audioRef.current.pause() : audioRef.current.play();
                }
                setIsPlaying((p) => !p);
              }}
            >
              {isPlaying ? "⏸" : "▶"}
            </button>
            <button className="ctrl-btn" onClick={handleNext}>⏭</button>
          </div>

          <div className="progress-wrap">
            <span className="progress-time">{fmt(elapsed)}</span>
            <div
              className="progress-bar"
              onClick={(e) => {
                if (!audioRef.current || !audioRef.current.duration) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const pct  = (e.clientX - rect.left) / rect.width;
                audioRef.current.currentTime = pct * audioRef.current.duration;
              }}
            >
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-time">
              {duration > 0 ? fmt(duration) : "--:--"}
            </span>
          </div>

          <button className="player-speed">1X</button>
        </div>
      )}

      {/* ── CART DRAWER ─────────────────────────────────────────────────────── */}
      {cartOpen && isLoggedIn && (
        <>
          <div className="cart-overlay" onClick={() => setCartOpen(false)} />
          <div className="cart-drawer">
            <div className="cart-header">
              <h2>🛒 CART</h2>
              <button className="cart-close" onClick={() => setCartOpen(false)}>✕ Close</button>
            </div>
            <div className="cart-items">
              {cart.length === 0 && (
                <p className="cart-empty">Your cart is empty.</p>
              )}
              {cart.map((item) => (
                <div key={item.cartKey} className="cart-item">
                  <div className="cart-item-info">
                    <div className="cart-item-name">{beatName(item)}</div>
                    <div className="cart-item-type">
                      {item.licenseType === "premium" ? "⭐ Premium License" : "📄 Lease License"}
                    </div>
                  </div>
                  <div className="cart-item-price">
                    ${Number(item.cartAmount).toFixed(2)}
                  </div>
                  <button
                    className="cart-remove"
                    onClick={() => removeFromCart(item.cartKey)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div className="cart-footer">
                <p className="cart-limit-note">
                  💡 One beat per checkout. Remove to swap.
                </p>
                <div className="cart-total">
                  <span>Total</span>
                  <span style={{ color: "var(--accent)" }}>
                    ${totalCart.toFixed(2)}
                  </span>
                </div>
                <button
                  className="btn-primary"
                  style={{ width: "100%" }}
                  onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── ORDERS DRAWER ───────────────────────────────────────────────────── */}
      {ordersOpen && isLoggedIn && (
        <>
          <div className="orders-overlay" onClick={() => setOrdersOpen(false)} />
          <div className="orders-drawer">

            <div className="orders-drawer-header">
              <h2>📋 MY ORDERS</h2>
              <div style={{ display: "flex", alignItems: "center" }}>
                <button className="orders-refresh" onClick={fetchOrders} title="Refresh">⟳ Refresh</button>
                <button className="cart-close" onClick={() => setOrdersOpen(false)}>✕ Close</button>
              </div>
            </div>

            <div className="orders-tabs">
              <button
                className={`orders-tab${ordersTab === "pending" ? " active" : ""}`}
                onClick={() => setOrdersTab("pending")}
              >
                Pending
                {pendingOrders.length > 0 && (
                  <span className="tab-badge">{pendingOrders.length}</span>
                )}
              </button>
              <button
                className={`orders-tab${ordersTab === "complete" ? " active" : ""}`}
                onClick={() => setOrdersTab("complete")}
              >
                Complete
                {completeOrders.length > 0 && (
                  <span className="tab-badge">{completeOrders.length}</span>
                )}
              </button>
            </div>

            <div className="orders-list">
              {ordersLoading ? (
                <div className="orders-loading">
                  <div className="orders-spinner" />
                  Loading orders…
                </div>
              ) : ordersTab === "pending" ? (
                pendingOrders.length === 0 ? (
                  <div className="orders-empty">
                    <div className="orders-empty-icon">⏳</div>
                    No pending orders.
                  </div>
                ) : (
                  pendingOrders.map((order, i) => (
                    <div key={order.id || i} className="order-card">
                      <div className="order-card-icon">🎵</div>
                      <div className="order-card-body">
                        <div className="order-card-name">
                          Order <span style={{color:"var(--accent)",fontFamily:"monospace"}}>#{order.orderid || order.id}</span>
                        </div>
                        <div className="order-card-meta">
                          <span className="order-card-amount">
                            ${Number(order.amount || 0).toFixed(2)}
                          </span>
                          <span className="order-card-version">
                            Product #{order.productid}
                          </span>
                          <span className="order-card-status pending">⏳ Pending</span>
                        </div>
                        <div className="order-card-date">
                          {order.created_at ? new Date(order.created_at).toLocaleString() : ""}
                        </div>
                      </div>
                      <div className="order-card-actions">
                        <button className="btn-pay-pending" onClick={() => handlePayPending(order)}>💳 Pay Now</button>
                        <button
                          className="btn-delete-order"
                          onClick={() => handleDeleteOrder(order)}
                          disabled={deletingId === order.id}
                        >
                          {deletingId === order.id ? "..." : "🗑 Delete"}
                        </button>
                      </div>
                    </div>
                  ))
                )
              ) : (
                completeOrders.length === 0 ? (
                  <div className="orders-empty">
                    <div className="orders-empty-icon">✅</div>
                    No completed orders yet.
                  </div>
                ) : (
                  completeOrders.map((order, i) => {
                    return (
                      <div key={order.id || i} className="order-card">
                        <div className="order-card-icon">🎵</div>
                        <div className="order-card-body">
                          <div className="order-card-name">
                            Order <span style={{color:"var(--accent)",fontFamily:"monospace"}}>#{order.orderid || order.id}</span>
                          </div>
                          <div className="order-card-meta">
                            <span className="order-card-amount">
                              ${Number(order.amount || 0).toFixed(2)}
                            </span>
                            <span className="order-card-version">
                              Product #{order.productid}
                            </span>
                            <span className="order-card-status complete">✅ Paid</span>
                          </div>
                          <div className="order-card-date">
                            {order.created_at ? new Date(order.created_at).toLocaleString() : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
              )}
            </div>
          </div>
        </>
      )}

      {/* ── PENDING ORDER CHECKOUT MODAL ─────────────────────────────────────── */}
      {pendingCheckoutOpen && pendingCheckoutItem && (
        <div className="modal-overlay" onClick={() => !checkoutLoading && setPendingCheckoutOpen(false)}>
          <div className="checkout-modal-box" onClick={(e) => e.stopPropagation()}>
            {!checkoutLoading && (
              <button className="modal-close" onClick={() => {
                setPendingCheckoutOpen(false);
                setOrdersOpen(true);
              }}>
                ✕ Close
              </button>
            )}
            <h2 className="checkout-title">PAY <span>ORDER</span></h2>
            <p className="checkout-subtitle">Complete your pending payment below.</p>

            <div className="checkout-summary">
              <div>
                <div className="checkout-summary-name">
                  Order <span style={{fontFamily:"monospace",color:"var(--accent)"}}>#{pendingCheckoutItem.orderid || pendingCheckoutItem.id}</span>
                </div>
                <div className="checkout-summary-meta">
                  Product #{pendingCheckoutItem.productid} · {pendingCheckoutItem.status === "pending" ? "⏳ Pending Payment" : ""}
                </div>
              </div>
              <div className="checkout-summary-price">
                ${finalPendingAmount.toFixed(2)}
              </div>
            </div>

            <div className="checkout-fields">
              <div className="checkout-row">
                <div className="checkout-field">
                  <label>First Name</label>
                  <input type="text" value={userData.fullName || ""} readOnly />
                </div>
                <div className="checkout-field">
                  <label>Last Name</label>
                  <input type="text" value={userData.fullName || ""} readOnly />
                </div>
              </div>
              <div className="checkout-field">
                <label>Email</label>
                <input type="email" value={userData.email || ""} readOnly />
              </div>
              <div className="checkout-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 0113060715"
                />
              </div>

              {/* Promo Code Input Section */}
              <div className="checkout-field">
                <label>Promo Code</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ padding: "0 1rem", whiteSpace: "nowrap" }}
                    onClick={handleValidatePromoCode}
                    disabled={promoValidating}
                  >
                    {promoValidating ? "Checking..." : "Validate"}
                  </button>
                </div>
                {promoAppliedMessage && (
                  <div style={{ fontSize: "0.75rem", color: discountAmount > 0 ? "var(--green)" : "var(--red)", marginTop: "4px" }}>
                    {promoAppliedMessage} {discountAmount > 0 ? `(-$${discountAmount.toFixed(2)})` : ""}
                  </div>
                )}
              </div>

              <div className="checkout-row">
                <div className="checkout-field">
                  <label>Version</label>
                  <input type="text" value={pendingCheckoutItem.version || pendingCheckoutItem.licenseType || "lease"} readOnly />
                </div>
                <div className="checkout-field">
                  <label>Amount (USD)</label>
                  <input type="text" value={`$${finalPendingAmount.toFixed(2)}`} readOnly />
                </div>
              </div>
            </div>

            <div className="checkout-actions">
              <button
                className="btn-ghost"
                onClick={() => { setPendingCheckoutOpen(false); setOrdersOpen(true); }}
                disabled={checkoutLoading}
              >
                ← Back
              </button>
              <button
                className="btn-checkout-submit"
                onClick={handlePendingCheckout}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? <><span className="checkout-spinner" /> Processing…</> : `Pay $${finalPendingAmount.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CHECKOUT MODAL ──────────────────────────────────────────────────── */}
      {checkoutOpen && cartItem && (
        <div className="modal-overlay" onClick={() => !checkoutLoading && setCheckoutOpen(false)}>
          <div className="checkout-modal-box" onClick={(e) => e.stopPropagation()}>
            {!checkoutLoading && (
              <button className="modal-close" onClick={() => setCheckoutOpen(false)}>
                ✕ Close
              </button>
            )}
            <h2 className="checkout-title">COMPLETE <span>PURCHASE</span></h2>
            <p className="checkout-subtitle">Review your order and confirm payment.</p>

            <div className="checkout-summary">
              <div>
                <div className="checkout-summary-name">{beatName(cartItem)}</div>
                <div className="checkout-summary-meta">
                  {cartItem.licenseType === "premium" ? "⭐ Premium License" : "📄 Lease License"}
                </div>
              </div>
              <div className="checkout-summary-price">
                ${finalCartAmount.toFixed(2)}
              </div>
            </div>

            <div className="checkout-fields">
              <div className="checkout-row">
                <div className="checkout-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={userData.fullName || ""}
                    readOnly
                    placeholder="Full name"
                  />
                </div>
                <div className="checkout-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={userData.fullName || ""}
                    readOnly
                    placeholder="Full name"
                  />
                </div>
              </div>

              <div className="checkout-field">
                <label>Email</label>
                <input type="email" value={userData.email || ""} readOnly />
              </div>

              <div className="checkout-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 0113060715"
                />
              </div>

              {/* Promo Code Input Section */}
              <div className="checkout-field">
                <label>Promo Code</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ padding: "0 1rem", whiteSpace: "nowrap" }}
                    onClick={handleValidatePromoCode}
                    disabled={promoValidating}
                  >
                    {promoValidating ? "Checking..." : "Validate"}
                  </button>
                </div>
                {promoAppliedMessage && (
                  <div style={{ fontSize: "0.75rem", color: discountAmount > 0 ? "var(--green)" : "var(--red)", marginTop: "4px" }}>
                    {promoAppliedMessage} {discountAmount > 0 ? `(-$${discountAmount.toFixed(2)})` : ""}
                  </div>
                )}
              </div>

              <div className="checkout-row">
                <div className="checkout-field">
                  <label>Version</label>
                  <input type="text" value={cartItem.licenseType === "premium" ? "Premium" : "Lease"} readOnly />
                </div>
                <div className="checkout-field">
                  <label>Amount (USD)</label>
                  <input type="text" value={`$${finalCartAmount.toFixed(2)}`} readOnly />
                </div>
              </div>
            </div>

            <div className="checkout-actions">
              <button
                className="btn-ghost"
                onClick={() => { setCheckoutOpen(false); setCartOpen(true); }}
                disabled={checkoutLoading}
              >
                ← Back
              </button>
              <button
                className="btn-checkout-submit"
                onClick={handleCheckout}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? <><span className="checkout-spinner" /> Processing…</> : `Pay $${finalCartAmount.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PESAPAL PAYMENT IFRAME MODAL ────────────────────────────────────── */}
      {pesapalUrl && (
        <div className="pesapal-overlay">
          <div className="pesapal-modal">
            <div className="pesapal-header">
              <div className="pesapal-header-left">
                <div className="pesapal-logo-dot" />
                <div>
                  <div className="pesapal-header-title">SECURE <span>PAYMENT</span></div>
                  <div className="pesapal-header-secure">🔒 Powered by Pesapal · SSL Encrypted</div>
                </div>
              </div>
              <button className="pesapal-close-btn" onClick={() => { setPesapalUrl(null); setIframeLoaded(false); }}>✕ Close</button>
            </div>
            <div className="pesapal-iframe-wrap">
              <div className={`pesapal-loading-cover${iframeLoaded ? " hidden" : ""}`}>
                <div className="pesapal-iframe-spinner" />
                <div className="pesapal-loading-text">Loading payment page…</div>
              </div>
              <iframe src={pesapalUrl} title="Pesapal Payment" allow="payment" onLoad={() => setIframeLoaded(true)} />
            </div>
            <div className="pesapal-footer">
              <span>✓</span> Your payment is secured by Pesapal. Do not close this window until payment is confirmed.
            </div>
          </div>
        </div>
      )}

      {/* ── LICENSING MODAL ─────────────────────────────────────────────────── */}
      {licenseOpen && (
        <div className="modal-overlay" onClick={() => setLicenseOpen(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setLicenseOpen(false)}>✕ Close</button>
            <h2 className="modal-title">LICENSE <span>TYPES</span></h2>
            <p className="modal-subtitle">Choose the right license for your project.</p>
            <div className="license-grid">
              <div className="license-card">
                <div className="license-type">Lease License</div>
                <p className="license-desc">Perfect for independent releases and growing artists.</p>
                <ul className="license-perks">
                  {LEASE_PERKS.map((perk, i) => (
                    <li key={i}><span className="check">✓</span>{perk}</li>
                  ))}
                </ul>
              </div>
              <div className="license-card premium">
                <div className="license-type">Premium License</div>
                <p className="license-desc">Full ownership and exclusive rights — take it to the top.</p>
                <ul className="license-perks">
                  {PREMIUM_PERKS.map((perk, i) => {
                    const isExclusive = perk === "Exclusive Ownership";
                    return (
                      <li key={i} className={isExclusive ? "exclusive" : ""}>
                        <span className="check">✓</span>{perk}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PHOTO ZOOM OVERLAY ────────────────────────────────────────────── */}
      {zoomedPhoto && (
        <div className="photo-zoom-overlay" onClick={() => setZoomedPhoto(null)}>
          <button className="photo-zoom-close" onClick={() => setZoomedPhoto(null)}>✕ Close</button>
          <img src={zoomedPhoto} alt="Zoomed Cover" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

    </>
  );
};

export default LandingPage;